import { Button } from "@funded-here-interface/common";
import {
  Group,
  Modal,
  Text,
  Button as MButton,
  MantineProvider,
  Radio,
  Center,
  Loader,
} from "@mantine/core";
import { FC, useEffect, useRef, useState, CSSProperties } from "react";
import { useStyle } from "./StructureNote.styles";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { REST_SERVER, WEB_DOMAIN } from "../../constants/domain";
import { useSubcriptionDocuSign } from "../../hooks/useDocuSign";
import usePostSubscriptionAgreementSigned from "../../hooks/usePostSubscriptionAgreementSigned";
import { COLORS } from "../../constants/colors";
import useOfferRoom from "../../hooks/useOfferRoom";
import useInvestorFunds from "../../hooks/useInvestorAccount";
import VQRVerifyPage from "../../components/VQR/VQRVerifyPage";
import { DocuSignEvent, DocuSignEventHandler } from "../../constants/docuSign";
import { getApiResponseErrorMsg } from "@funded-here-interface/common/src/Utils/apiHelper";
import { useNavigate } from "react-router-dom";
import MembershipAgreement from "../../components/MembershipAgreement/MembershipAgreement";
import { useDisclosure } from "@mantine/hooks";
import { EntityTypeEnum } from "@funded-here-interface/common/src/constant/enum";
import DocViewer from "@cyntler/react-doc-viewer";
import { ENV } from "@funded-here-interface/common/src/constant/constant";
import SignQRModal from "../../components/SignQRModal/SignQRModal";
import useSignedDocument from "../../hooks/useSignedDocument";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import useInvestorGA from "../../hooks/useInvestorGA";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";

declare global {
  interface Window {
    DocuSign: any;
  }
}

interface InvestmentOpportunity {
  id: number;
  currency: string;
  tenor: string;
  amountAvailableForInvestment: string;
  snInterestRate: string;
  totalSKU: number;
  totalMerchants: number;
}

const StructureNote: FC = () => {
  const { classes } = useStyle();
  const [category, setCategory] = useState("view");
  const [offerRoomData, setOfferRoomData] = useState<any>([]);
  const [accountData, setAccountData] = useState<any>({
    accountBalance: { SGD: 0, USD: 0 },
  });
  const [accountGAData, setAccountGAData] = useState<any>({
    accountBalance: { SGD: 0, USD: 0 },
  });
  const docuSignMutation = useSubcriptionDocuSign();
  const offerRoomMutation = useOfferRoom();
  const InvestorFundsMutation = useInvestorFunds();
  const InvestorGAMutation = useInvestorGA();
  const [realUrl, setRealUrl] = useState("");
  const [documentId, setDocumentId] = useState(0);
  const [qrModalOpened, { open: openQrModal, close: closeQrModal }] =
    useDisclosure(false);
  const docs = [
    {
      uri: realUrl,
      fileType: "pdf",
    },
  ];

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [selectedData, setSelectedData] =
    useState<InvestmentOpportunity | null>(null);
  const { token, agreedToMembershipAgreement, isAuthRep, entityType } =
    useSelector((state: RootState) => state.auth);
  const hideNoteSubscribe =
    entityType === EntityTypeEnum.INSTITUTION && !isAuthRep;
  const [selectedCurrency, setSelectedCurrency] = useState("SGD");

  const subscriptionAgreementMutation = usePostSubscriptionAgreementSigned();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [investorSubscribeAmount, setInvestorSubscribeAmount] = useState("");
  const [selectedNoteCurrency, setSelectedNoteCurrency] = useState("");
  const [offerSigned, setOfferSigned] = useState(false);
  const navigation = useNavigate();
  const [opened, { close }] = useDisclosure(
    entityType === EntityTypeEnum.INSTITUTION
      ? isAuthRep === true && agreedToMembershipAgreement !== true
      : agreedToMembershipAgreement !== true
  );
  const [completedAt, setCompletedAt] = useState("");

  const handleAuthenticatedChange = (isAuthenticated: boolean) => {
    setIsAuthenticated(isAuthenticated);

    if (isAuthenticated) {
      handleOK();
    }
  };
  const signedDocument = useSignedDocument();

  const agreementRef = useRef(null);
  const [viewerKey, setViewerKey] = useState(0);

  useEffect(() => {
    // loadDocuSignScript();
    InvestorFundsMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          setAccountData(data);
        },
        onError: (error: any) => {
          toast.error(error);
        },
      }
    );
    InvestorGAMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          setAccountGAData(data);
        },
        onError: (error: any) => {
          toast.error(error);
        },
      }
    );
    offerRoomMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          setOfferRoomData(data);
        },
        onError: (error: any) => {
          toast.error(error);
        },
      }
    );
  }, []);

  //  Check inputValue matches the money format (positive number with up to two decimal places)
  const validateInvestorSubscribeAmount = (inputValue: string) => {
    const isValidFormat = /^\d*\.?\d{0,2}$/.test(inputValue);

    if (!isValidFormat) {
      return false;
    }

    const inputNumber = parseFloat(inputValue);
    const amountAvailable = parseFloat(
      selectedData?.amountAvailableForInvestment || "0"
    );

    if (inputNumber > amountAvailable) {
      return false;
    }

    return true;
  };

  const handleOK = async () => {
    if (!selectedData) {
      toast.error("No investment opportunity selected.");
      return;
    }

    toast.info("Document is loading");
    setIsOverlayVisible(true);

    docuSignMutation.mutate(
      {
        token,
        structuredNoteId: selectedData.id,
        investedAmount: investorSubscribeAmount,
        webDomain: WEB_DOMAIN,
        redirectUrl: `${REST_SERVER}/document-management/docusign/webhook`,
      },
      {
        onSuccess: async (data) => {
          // if (ENV === "production") {
          //   const { documentId, url } = data;

          //   window.DocuSign.loadDocuSign(documentId)
          //     .then((docusign: any) => {
          //       const signing = docusign.signing({
          //         url: url,
          //         displayFormat: "focused",
          //         style: {
          //           branding: {
          //             primaryButton: {
          //               backgroundColor: COLORS.primaryButtonBackground,
          //               color: COLORS.primaryButtonText,
          //             },
          //           },
          //           signingNavigationButton: {
          //             finishText: "Sign",
          //             position: "bottom-center",
          //           },
          //         },
          //       });
          //       signing.on(DocuSignEventHandler.READY, () => {
          //         toast.info("Sign the Document using E-signature");
          //       });

          //       signing.on(
          //         DocuSignEventHandler.SESSION_END,
          //         async (event: any) => {
          //           if (
          //             event.sessionEndType === DocuSignEvent.SIGNING_COMPLETE
          //           ) {
          //             setIsOverlayVisible(false);
          //             //Let backend to handle the webhook of the signing complete
          //             await handleSubscriptionAgreementSigned();
          //           } else {
          //             setIsOverlayVisible(false);
          //             toast.error("Signing incomplete, please try again");
          //           }
          //         }
          //       );

          //       if (agreementRef.current) {
          //         signing.mount(agreementRef.current);
          //       } else {
          //         toast.error("Agreement container not found");
          //       }
          //     })
          //     .catch((e: any) => {
          //       const message = getApiResponseErrorMsg(e);

          //       toast.error(message);
          //     });
          // } else {
          setRealUrl(data.url);
          setDocumentId(data.documentId);
          setCompletedAt(data.completedAt);
          // }
        },
        onError: (e) => {
          toast.error((e as Error).message);
          setIsOverlayVisible(false);
        },
      }
    );
  };
  const handleSubscriptionAgreementSigned = async () => {
    if (!selectedData) {
      toast.error("No investment opportunity selected.");
      return;
    }
    return new Promise<void>((resolve, reject) => {
      subscriptionAgreementMutation.mutate(
        {
          token,
          structuredNoteId: selectedData.id,
          investorSubscribedAmount: investorSubscribeAmount,
        },
        {
          onSuccess: () => {
            toast.success("Document Signed Successfully");
            setIsOverlayVisible(false);
            setOfferSigned(true);
            resolve();
          },
          onError: (e) => {
            toast.error((e as Error).message);
            setIsOverlayVisible(false);
            reject();
          },
        }
      );
    });
  };

  const handleBoxSelect = (data: InvestmentOpportunity) => {
    setSelectedData(data);
    setSelectedNoteCurrency(data.currency);

    switch (data.currency) {
      case "SGD":
        accountData.bankName = accountData.investorSgdBankName;
        accountData.accountHolder = accountData.investorSgdBankAccountHolder;
        accountData.accountNumber = accountData.investorSgdBankAccountId;
        accountData.bicSwift = accountData.investorSgdBicSwift;
        accountData.branchCode = accountData.investorSgdBranchCode;
        accountData.bankCode = accountData.investorSgdBankCode;
        accountData.bankAddress = accountData.investorSgdBankAddress;
        break;
      case "USD":
        accountData.bankName = accountData.investorUsdBankName;
        accountData.accountHolder = accountData.investorUsdBankAccountHolder;
        accountData.accountNumber = accountData.investorUsdBankAccountId;
        accountData.bicSwift = accountData.investorUsdBicSwift;
        accountData.branchCode = accountData.investorUsdBranchCode;
        accountData.bankCode = accountData.investorUsdBankCode;
        accountData.bankAddress = accountData.investorUsdBankAddress;
        break;
    }

    setAccountData(accountData);
  };

  const handleSubscribeClick = () => {
    if (investorSubscribeAmount === "") {
      toast.error("Please enter an investment/subscription amount.");
      return;
    }

    setCategory("subscribe");
  };

  const getBoxStyle = (data: InvestmentOpportunity): CSSProperties => ({
    cursor: "pointer",
    border: "1px solid #ccc",
    padding: "30px",
    borderRadius: "5px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    flex: 1,
    marginTop: "20px",
    display: "flex",
    flexDirection: "column",
    gap: 25,
    margin: "10px",
    backgroundColor:
      selectedData && selectedData.id === data.id
        ? "rgba(0, 0, 0, 0.25)"
        : hoveredId === data.id
        ? "rgba(0, 0, 0, 0.25)"
        : "transparent",
    color:
      hoveredId === data.id || (selectedData && selectedData.id === data.id)
        ? COLORS.whiteColor
        : undefined,
    transition: "background-color 0.3s ease",
  });

  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const canNavigateBackward = visibleStartIndex > 0;
  const canNavigateForward = visibleStartIndex < offerRoomData.length - 3;

  const handleNext = () => {
    if (canNavigateForward) {
      setVisibleStartIndex(visibleStartIndex + 1);
    }
  };

  const handlePrev = () => {
    if (canNavigateBackward) {
      setVisibleStartIndex(visibleStartIndex - 1);
    }
  };
  const visibleItems = offerRoomData.slice(
    visibleStartIndex,
    visibleStartIndex + 3
  );

  const handleOnVerifyMeNext = () => {
    setIsAuthenticated(true);
  };

  const handleBackSubscribedNote = () => {
    navigation("/subscribed-invested-notes");
  };

  const handleSign = async (isAuthenticated: boolean) => {
    if (isAuthenticated) {
      await handleSubscriptionAgreementSigned();
      closeQrModal();
      setRealUrl("");
      signedDocument.mutate(
        { documentId, token },
        {
          onSuccess: async (data) => {
            // await handleSubscriptionAgreementSigned();

            setRealUrl(data.url);
            setDocumentId(data.documentId);
            setCompletedAt(data.completedAt);
            setViewerKey((prevKey) => prevKey + 1);
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    }
  };

  const openModal = () => {
    setViewerKey((prevKey) => prevKey + 1);
    openQrModal();
  };

  const closeModal = () => {
    setViewerKey((prevKey) => prevKey + 1);
    closeQrModal();
  };

  return (
    <>
      <div
        className={classes.stepContent}
        style={{ marginTop: "0px", paddingTop: "0px" }}
      >
        <MembershipAgreement
          toCompleteOnboard={false}
          onError={(value: string) => {
            toast.error(value);
          }}
          opened={opened}
          close={close}
        />
        <Text className={classes.headerText} style={{ marginTop: "10px" }}>
          {/* Hide the Offer Room title when authenticating */}
          {category === "subscribe" && isAuthenticated ? "" : "Offer Room"}
        </Text>

        {category === "view" && (
          <>
            <Text style={{ fontWeight: 400 }}>
              You may now deposit funds into your FundedHere Account and start
              your investment journey by subscribing to the investment notes
              below!{" "}
            </Text>
            <div>
              <Text
                style={{
                  fontWeight: 800,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                Virtual Account Details
              </Text>
              {!accountGAData?.investorSgdBankAccountId ? (
                <Text
                  style={{
                    color: "black",
                    fontStyle: "italic",
                    fontSize: 16, // Increase size for emphasis
                    textAlign: "left", // Center aligns for better visibility
                    padding: "10px 10px 10px 0px",
                  }}
                >
                  Account creation is being processed.
                </Text>
              ) : (
                <div style={{ display: "flex", gap: 30 }}>
                  <div
                    style={{
                      backgroundColor: "#D9D9D980",
                      borderRadius: 5,
                      padding: "10px 30px",
                    }}
                  >
                    <Text>
                      Bank Name: {accountGAData?.investorSgdBankName ?? "-"}
                    </Text>
                    <Text>
                      Account name:{" "}
                      {accountGAData?.investorSgdBankAccountHolder ?? "-"}
                    </Text>
                    <Text>
                      <span
                        style={{
                          color: COLORS.agreementFont,
                          fontWeight: "bold",
                        }}
                      >
                        SGD Account:{" "}
                      </span>
                      {accountGAData?.investorSgdBankAccountId ?? "-"}
                    </Text>
                    <Text>
                      Swift code: {accountGAData?.investorSgdBicSwift ?? "-"}
                    </Text>
                  </div>
                </div>
              )}
            </div>
            {/* <div>
              <Text
                style={{
                  fontWeight: 800,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                VA Account details
              </Text>
              <div style={{ display: "flex", gap: 30 }}>
                <div
                  style={{
                    backgroundColor: "#D9D9D980",
                    borderRadius: 5,
                    padding: "10px 30px",
                  }}
                >
                  <Text>
                    Bank Name: {accountGAData?.investorSgdBankName ?? "-"}
                  </Text>
                  <Text>
                    Account name:{" "}
                    {accountGAData?.investorSgdBankAccountHolder ?? "-"}
                  </Text>
                  <Text>
                    <span
                      style={{
                        color: COLORS.agreementFont,
                        fontWeight: "bold",
                      }}
                    >
                      SGD Account:{" "}
                    </span>
                    {accountGAData?.investorSgdBankAccountId ?? "-"}
                  </Text>
                  <Text>
                    Swift code: {accountGAData?.investorSgdBicSwift ?? "-"}
                  </Text>
                </div>
                <div>
                  <div
                    style={{
                      backgroundColor: "#D9D9D980",
                      borderRadius: 5,
                      padding: "10px 30px",
                    }}
                  >
                    <Text>
                      Bank Name: {accountGAData?.investorUsdBankName ?? "-"}
                    </Text>
                    <Text>
                      Account name:{" "}
                      {accountGAData?.investorUsdBankAccountHolder ?? "-"}
                    </Text>
                    <Text>
                      <span
                        style={{
                          color: COLORS.agreementFont,
                          fontWeight: "bold",
                        }}
                      >
                        USD Account:{" "}
                      </span>
                      {accountGAData?.investorUsdBankAccountId ?? "-"}
                    </Text>
                    <Text>
                      Swift code: {accountGAData?.investorUsdBicSwift ?? "-"}
                    </Text>
                  </div>
                </div>
              </div>
            </div> */}

            {/* <Radio.Group
              name="currency"
              label="Select account type"
              value={selectedCurrency}
              onChange={setSelectedCurrency}
            >
              <Group mt="xs">
                <Radio value="SGD" label="SGD" />
                <Radio value="USD" label="USD" />
              </Group>
            </Radio.Group> */}
            <div
              style={{
                display: "flex",
                flexDirection: "column", // Stack items vertically
                marginTop: 20,
                marginBottom: 10,
                gap: 10,
                alignItems: "flex-start", // Align items to the left
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: 20,
                  marginBottom: 10,
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{ fontWeight: 800, marginTop: 12, marginBottom: 10 }}
                >
                  Available Balance{" "}
                </Text>
                <Text
                  style={{
                    color: COLORS.agreementFont,
                    fontWeight: "bold",
                    fontSize: "22px",
                  }}
                >
                  <div style={{ display: "flex", gap: 10 }}>
                    {selectedCurrency === "SGD" && (
                      <span>
                        SGD{" "}
                        {formatNumbersWithThousandSeperators(
                          Number(accountData?.accountBalance?.SGD ?? "0"),
                          true,
                          undefined,
                          "SGD"
                        )}
                      </span>
                    )}
                    {selectedCurrency === "USD" && (
                      <span>
                        USD:{" "}
                        {parseFloat(
                          accountData?.accountBalance?.USD ?? "0"
                        ).toFixed(2)}
                      </span>
                    )}
                  </div>
                </Text>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
              }}
            >
              {offerRoomData?.length > 1 && canNavigateBackward && (
                <button
                  onClick={handlePrev}
                  style={{
                    cursor: canNavigateBackward ? "pointer" : "not-allowed",
                    color: COLORS.agreementFont,
                    marginRight: "10px",
                    padding: "10px",
                    fontSize: "30px",
                    fontWeight: "bolder",
                    borderRadius: "80px",
                    border: "none",
                  }}
                >
                  ←
                </button>
              )}

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  width: offerRoomData?.length > 1 ? "100%" : "350px",
                  justifyContent: "space-between",
                }}
              >
                {visibleItems.map((data: any) => (
                  <div
                    key={data.id}
                    onMouseEnter={() => setHoveredId(data.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={
                      hideNoteSubscribe
                        ? undefined
                        : () => handleBoxSelect(data)
                    }
                    style={getBoxStyle(data)}
                  >
                    <p style={{ fontWeight: "bold" }}>
                      Amount Available for
                      <br /> Investment
                    </p>
                    <p
                      style={{
                        color: COLORS.agreementFont,
                        fontWeight: "bold",
                        fontSize: "22px",
                      }}
                    >
                      {data?.currency}{" "}
                      {data?.amountAvailableForInvestment ?? "0"}
                    </p>
                    <p>Note : {data?.structuredNoteDisplayId}</p>
                    <p>Tenor: {data?.tenor} days</p>
                    <p>Interest Rate: {data?.snInterestRate} % p.a.</p>
                    <p>Total SKU’s: {data?.totalSKU}</p>
                    <p>Total Merchants: {data?.totalMerchants}</p>
                  </div>
                ))}
              </div>
              {offerRoomData?.length > 1 && canNavigateForward && (
                <button
                  onClick={handleNext}
                  style={{
                    cursor: canNavigateForward ? "pointer" : "not-allowed",
                    color: COLORS.agreementFont,
                    marginRight: "10px",
                    padding: "10px",
                    fontSize: "30px",
                    fontWeight: "bolder",
                    borderRadius: "80px",
                    border: "none",
                  }}
                >
                  →
                </button>
              )}
            </div>
            {selectedNoteCurrency !== "" && (
              <div>
                <Text
                  style={{
                    fontWeight: 800,
                    marginTop: 20,
                    marginBottom: 10,
                  }}
                >
                  {selectedNoteCurrency} Amount to Invest
                </Text>
                <input
                  type="text"
                  value={investorSubscribeAmount}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Check if the input is empty or matches the money format (positive number with up to two decimal places)
                    if (
                      inputValue === "" ||
                      validateInvestorSubscribeAmount(inputValue)
                    ) {
                      setInvestorSubscribeAmount(inputValue);
                    }
                  }}
                  style={{
                    padding: "7px 2px 7px 2px",
                    outline: "none",
                    borderColor: "#D9DCE1",
                    marginBottom: 20,
                    width: "40%",
                    borderRadius: 3,
                  }}
                />
              </div>
            )}

            {!hideNoteSubscribe && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                }}
              >
                <button
                  style={{
                    color: "white",
                    backgroundColor: COLORS.agreementFont,
                    borderRadius: "5px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    padding: "12px 80px",
                    fontSize: "20px",
                    border: "none",
                    cursor: selectedData ? "pointer" : "not-allowed",
                    opacity: selectedData ? 1 : 0.5,
                  }}
                  onClick={handleSubscribeClick}
                  disabled={
                    !selectedData &&
                    validateInvestorSubscribeAmount(investorSubscribeAmount)
                  }
                >
                  Subscribe
                </button>

                <ToastContainer />
              </div>
            )}
          </>
        )}

        {category === "subscribe" && !isAuthenticated && (
          <VQRVerifyPage
            onNext={handleOnVerifyMeNext}
            onAuthenticatedChange={handleAuthenticatedChange}
          />
        )}

        {category === "subscribe" && isAuthenticated && (
          <div className={classes.SubscribeOutter}>
            <div>
              <div className={classes.subscriptionAgreement}>
                <Text className={classes.aggrementTitle}>
                  Sign Subscription Agreement
                </Text>
                {/* {ENV === "production" && (
                  <Text className={classes.linkText}>Sign with Docusign</Text>
                )} */}
              </div>
              <Text>
                Please review and sign the subscription agreement attached.
              </Text>
            </div>

            <div className={classes.fundAccount}>
              <Text className={classes.fundAccountText}>Fund your account</Text>
              <Text>
                Please make sure you have transferred up to{" "}
                {selectedNoteCurrency} {investorSubscribeAmount} into your
                account
              </Text>
              <div className={classes.paymentDetails}>
                <Text>Payment Details:</Text>
                <ul className={classes.listControl}>
                  <li>
                    Bank Name: {accountGAData?.investorSgdBankName ?? "N/A"}
                  </li>
                  <li>
                    Account Holder:{" "}
                    {accountGAData?.investorSgdBankAccountHolder ?? "N/A"}
                  </li>
                  <li>
                    Account Number:{" "}
                    {accountGAData?.investorSgdBankAccountId ?? "N/A"}
                  </li>
                  <li>
                    BIC/SWIFT: {accountGAData?.investorSgdBicSwift ?? "N/A"}
                  </li>
                  <li>
                    Branch Code: {accountGAData?.investorSgdBranchCode ?? "N/A"}
                  </li>
                  <li>
                    Bank Code: {accountGAData?.investorSgdBankCode ?? "N/A"}
                  </li>
                  <li>
                    Bank Address:{" "}
                    {accountGAData?.investorSgdBankAddress ?? "N/A"}
                  </li>
                </ul>
              </div>
            </div>
            {/* {isOverlayVisible && ENV === "production" && (
              <div className={classes.overlay}>
                <div
                  className={classes.document}
                  id="agreement"
                  ref={agreementRef}
                >
                </div>
              </div>
            )} */}
            <ToastContainer />
            {/* {ENV === "production" && (
              <div className={classes.buttonWrapper}>
                {offerSigned ? (
                  <Button
                    onClick={handleBackSubscribedNote}
                    backgroundColor="#338F86"
                    textColor="#ffff"
                    children="Subscribed Notes"
                    border="1px solid #338F86"
                    width="300px"
                  />
                ) : (
                  <Button
                    onClick={handleOK}
                    backgroundColor="#338F86"
                    textColor="#ffff"
                    children="Sign Agreement"
                    border="1px solid #338F86"
                    width="300px"
                  />
                )}
              </div>
            )} */}

            {realUrl !== "" && (
              <>
                <DocViewer
                  documents={docs}
                  style={{ width: "100%", height: 500, overflow: "auto" }}
                  className={classes.docviewer}
                  key={viewerKey}
                  config={{
                    header: {
                      disableHeader: false,
                      disableFileName: true,
                      retainURLParams: false,
                    },
                    pdfVerticalScrollByDefault: true, // false as default
                  }}
                />

                {completedAt === "" && (
                  <div className={classes.buttonContainer}>
                    <Button
                      onClick={openModal}
                      textColor="white"
                      backgroundColor="#338F86"
                      children="Proceed to Sign"
                    />
                  </div>
                )}

                {completedAt !== "" && (
                  <div className={classes.buttonContainer}>
                    <Button
                      onClick={handleBackSubscribedNote}
                      textColor="white"
                      backgroundColor="#338F86"
                      children="Subscribed Notes"
                    />
                  </div>
                )}
              </>
            )}

            {realUrl === "" && (
              <div>
                <Center maw={400} h={500} mx="auto">
                  <Loader />
                </Center>
              </div>
            )}

            <SignQRModal
              opened={qrModalOpened}
              close={closeModal}
              onSignVerify={handleSign}
            />
          </div>
        )}
      </div>
    </>
  );
};
export default StructureNote;
